import React from 'react'
import { isEqual } from 'lodash'
import { filterSelectedMetrics } from '../../charts/utils';
import { TextInput } from './TextInput'
import ColorPicker from 'components/ColorPicker'
import { Select } from './Select'
import { MultiSelect } from './MultiSelect'
import { SingleSelect } from './SingleSelect'
import { NumberInput } from './NumberInput';
import { rgbToHexAlpha } from '../../../../../../utils/format';
import classNames from 'classnames';
import { TextAreaInput } from './TextAreaInput';

const CHART_INSIGHT_MODE = {
  DEFAULT: 'Briefing',
  OPTIONS: [
    { value: 'briefing', label: 'Briefing' },
    { value: 'keyFinding', label: 'Key Finding' },
  ]
}


export const ThresholdInput = ({
  color = '',
  isDisabled,
  onChange = (val) => null
}) => {

  return (
    <div>This is Threshold Input</div>
  )
}

export const ColorTiersInput = ({
  color = '',
  isDisabled,
  onChange = (val) => null
}) => {

  return (
    <div>This is Color Tiers Input</div>
  )
}

export const ChartIntervalInput = ({
  placeholder = '',
  label = '',
  intervalQuantity = 0,
  selectedIntervalOption = {},
  intervalOptions = [],
  onChange = (val) => null
}) => {

  return (
    <div className='custom-config-input custom-config-input--chart-interval'>
      <NumberInput
        label={label}
        placeholder={placeholder}
        value={intervalQuantity}
        onChange={(val) => {
          onChange('quantity', val)
        }}
      />
      <Select
        placeholder={'Display text'}
        selectedOption={selectedIntervalOption}
        options={intervalOptions}
        onChange={(val) => {
          onChange('value', val.value)
        }}
      />
    </div>
  )
}

export const TextColorInput = ({
  placeholder = '',
  textPlaceholder = '',
  label = '',
  hasBorder = false,
  value = '',
  color = '',
  text = '',
  onChange = (val) => null,
  disabled,
  colorDisabled = false,
  workspace,
  showTextInput,
  chartValues,
  hideColorPicker = false,
  isPercent = false,
  theme = ''
}) => {

  const normalizeValue = (v) => {
    if (typeof v !== 'string') return v
    // Strip any trailing % and whitespace for storage
    return v.replace(/%\s*$/, '')
  }

  const handleOnChangeTextInput = (newValue) => {
    let cleaned = normalizeValue(newValue)

    if (!isEqual(value, cleaned)) {
      onChange({ value: cleaned, color, text })
    }
  }

  const handleOnChangeColorPicker = (newColor) => {
    if (!isEqual(color, newColor)) {
      onChange({ value, color: newColor, text })
    }
  }

  const handleOnChangeTextInputText = (newValue) => {
    if (!isEqual(text, newValue)) {
      onChange({ value, color, text: newValue })
    }
  }

  return (
    <>
      {hasBorder ? <div className="chart-editor-border">
        <>
          <div className='custom-config-input'>
            <TextInput
              label={label}
              placeholder={placeholder}
              value={isPercent && value !== '' && value !== null && value !== undefined ? `${value}%` : value}
              isDisabled={disabled}
              onChange={handleOnChangeTextInput}
              color={theme}
            />
            {!hideColorPicker && (
              <ColorPicker
                isDisabled={disabled || colorDisabled}
                theme={workspace?.color}
                onChange={(data) => handleOnChangeColorPicker(rgbToHexAlpha(data.rgb))}
                colorHex={(disabled || colorDisabled) ? '#8b959e' : color}
              />
            )}
          </div>
          {showTextInput && <div className='custom-config-input'>
            <TextInput
              label={''}
              noLabel
              placeholder={textPlaceholder}
              value={text}
              isDisabled={disabled}
              onChange={handleOnChangeTextInputText}
              color={theme}
            />
          </div>}
        </>
      </div>
        :
        <>
          <div className='custom-config-input'>
            <TextInput
              label={label}
              placeholder={placeholder}
              value={isPercent && value !== '' && value !== null && value !== undefined ? `${value}%` : value}
              isDisabled={disabled}
              onChange={handleOnChangeTextInput}
              color={theme}
            />
            {!hideColorPicker && (
              <ColorPicker
                isDisabled={disabled || colorDisabled}
                theme={workspace?.color}
                onChange={(data) => handleOnChangeColorPicker(rgbToHexAlpha(data.rgb))}
                colorHex={(disabled || colorDisabled) ? '#8b959e' : color}
              />
            )}
          </div>
          {showTextInput && <div className='custom-config-input'>
            <TextInput
              label={''}
              noLabel
              placeholder={textPlaceholder}
              value={text}
              isDisabled={disabled}
              onChange={handleOnChangeTextInputText}
              color={theme}
            />
          </div>}
        </>
      }

    </>
  )
}

export const MetricInput = ({
  placeholder = '',
  label = '',
  metricOptions = [],
  selectedMetrics = [],
  selectedTableName = '',
  metricsConfig = {},
  onChangeSelectedMetrics = (val) => null,
  onChangeMetricConfig = (val) => null,
  workspace,
  chartType,
  showSubConfig = false,
  chartValues,
  id
}) => {

  let options = selectedTableName ? filterSelectedMetrics(selectedTableName, metricOptions) : metricOptions

  if (id === 'groupBy' && (chartType === 'pie' || chartType === 'gauge' || chartType === 'doughnut' || chartType === 'card')) {
    options = options.map(opt => ({
      ...opt,
      metrics: opt.metrics.filter(metric => metric.name !== "time")
    }));
  }

  const handleMultiSelectChange = (selectedOptions) => {
    const newConfig = {}
    selectedOptions.forEach(option => {
      newConfig[option.value] = metricsConfig[option.value] || {
        value: '',
        color: ''
      }
    })
    onChangeSelectedMetrics(selectedOptions, newConfig)
  }

  const handleChangeMetricConfig = (metric, selectedOptions) => {
    const newConfig = {
      ...metricsConfig,
      [metric]: selectedOptions
    }
    // selectedOptions.forEach(option => {
    //   newConfig[option.value] = metricsConfig[option.value] || {
    //     value: '',
    //     color: ''
    //   }
    // })
    onChangeMetricConfig(selectedOptions, newConfig)
  }

  return (
    <div className={classNames(showSubConfig && "gap-8")}>
      {/**
       * Some analysis modes require multiple metrics. In those cases,
       * do not force single-select even for card charts or table raw mode.
       */}
      {(() => {
        const requiresMultiple = [
          'sumColumns',
          'subtractColumns',
          'multiplyColumns',
          'divideColumns',
          'ratioOfTwoMetrics',
          'cumulativeTotal',
          'minimumValue',
          'maximumValue',
          'averageValue',
          'allTrue',
          'anyTrue',
        ].includes(chartValues?.analysisMode)
        const isSingle = (chartType === 'card' && !requiresMultiple) || (chartType === 'table' && chartValues?.tableColumnMetricData === 'raw') || chartType === 'map'
        return (
          <MultiSelect
            label={label}
            defaultSelection={chartType === 'line' || chartType === 'area' ? 'time' : ''}
            placeholder={placeholder}
            withMetricDataMenu={true}
            selectedOptions={selectedMetrics}
            validTableName={selectedTableName}
            options={options}
            isSingleSelect={isSingle}
            onChange={handleMultiSelectChange}
          />
        )
      })()}
      {showSubConfig &&
        <div className={classNames("border-left", { 'sub-config-gap': true })}>
          {Object.entries(metricsConfig).map(([metric, config]) => {
            return (
              <TextColorInput
                key={`${config}_${metric}`}
                label={metric}
                value={config?.value}
                color={config?.color}
                disabled={false}
                // itemConfig={config}
                onChange={value => {
                  handleChangeMetricConfig(metric, value)
                }
                }
                workspace={workspace}
              />
            )
          })}
        </div>
      }
    </div>
  )
}

export const MetricInputByCategory = ({
  placeholder = '',
  label = '',
  metricOptions = [],
  selectedMetrics = {},
  selectedGroup = {},
  selectedTableName = '',
  metricsConfig = {},
  onChangeSelectedMetrics = (val) => null,
  onChangeMetricConfig = (val) => null,
  workspace,
  chartType,
  showSubConfig = false,
  id
}) => {

  let options = selectedTableName ? filterSelectedMetrics(selectedTableName, metricOptions) : metricOptions

  if (id === 'groupBy' && (chartType === 'pie' || chartType === 'gauge' || chartType === 'doughnut' || chartType === 'card')) {
    options = options.map(opt => ({
      ...opt,
      metrics: opt.metrics.filter(metric => metric.name !== "time")
    }));
  }

  const handleSelectChange = (selected) => {
    // Normalize selected into an array for consistency
    const selectedArray = Array.isArray(selected) ? selected : (selected ? [selected] : [])

    const newConfig = {}
    selectedArray.forEach(option => {
      newConfig[option.value] = metricsConfig[option.value] || {
        value: '',
        color: '',
      }
    })

    onChangeSelectedMetrics(selectedArray, newConfig)
  }

  const handleGroupChange = (selected) => {
    // Normalize selected into an array for consistency
    const selectedArray = Array.isArray(selected) ? selected : (selected ? [selected] : [])

    const newConfig = {}
    selectedArray.forEach(option => {
      newConfig[option.value] = metricsConfig[option.value] || {
        value: '',
        color: '',
      }
    })

    onChangeSelectedMetrics(selectedArray, newConfig, true)
  }

  return (
    <React.Fragment>
      <SingleSelect
        label={label}
        placeholder={placeholder}
        withMetricDataMenu={true}
        selectedOption={selectedMetrics}
        validTableName={selectedTableName}
        options={options}
        isSingleSelect={chartType === 'card'}
        onChange={handleSelectChange}
      />
      <div className="border-left">
        <SingleSelect
          label={'Breakdown by'}
          placeholder={'Select group'}
          withMetricDataMenu={true}
          selectedOption={selectedGroup}
          validTableName={selectedTableName}
          options={options}
          isSingleSelect={chartType === 'card'}
          onChange={handleGroupChange}
        />
      </div>
    </React.Fragment>
  )
}

export const DeviceInput = ({
  placeholder = '',
  label = '',

  deviceOptions = [],
  selectedDevices = [],
  workspace
}) => {



  return (
    <MultiSelect
      key={field.id}
      label={label}
      placeholder={placeholder}
      selectedOptions={getMultiSelectSelectedOption(value)}
      options={props?.devices || []}
      onChange={val => {
        const newValue = val.map(opt => opt.value)
        props?.onChangeField(fieldId, newValue)
      }}
      withSimpleMenu={true}
      showSelectedOptions={true}
      withDashboardDefaultOption={true}
    />
  )
}

export const SingleColorSelector = ({
  label = '',
  placeholder = '',
  onChangeColorSelector = (val) => null,
  workspace,
  id,
  value,
  color,
}) => {


  const onChange = (selectedOptions) => {
    onChangeColorSelector(selectedOptions)
  }

  return (
    <div className="gap-8">
      <TextColorInput
        label={label}
        value={value.value}
        color={value.color}
        onChange={value => {
          onChange(value)
        }
        }
        workspace={workspace}
      />
    </div>
  )
}